import React, { useRef, useState } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'
import { makeStyles } from 'tss-react/mui'
import { useTheme } from '@mui/material/styles'
import Carousel, { ControlProps } from 'nuka-carousel'
import { ButtonBase, Container, Typography, useMediaQuery } from '@mui/material'
import CarouselArrowLeftIcon from '@icons/CarouselArrowLeftIcon'
import CarouselArrowRightIcon from '@icons/CarouselArrowRightIcon'
import { sendCarouselPromoEvent } from '@src/utils/analytics/carouselAnalytics'
import useResponsiveSizes from '@utils/useResponsiveSizes'
import {
  CarouselTitle,
  INTERSECTION_OBSERVER_THRESHOLD,
} from '@src/components/DynamicProductCarousel/DynamicProductCarousel'
import {
  ProductCardProps,
  CarouselProduct,
  CarouselProductTypes,
} from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import { handleSlideMovement } from '@src/components/DynamicProductCarousel/slideMovementHandler'
import NewPdpDynamicProductCard from './NewPdpDynamicProductCard'
import { useCategorizePage } from '@src/utils/analytics/CategorizePage'

const useStyles = makeStyles<void, 'productHeroTile'>()((
  theme,
  _params,
  classes
) => {
  return {
    withoutControls: {
      padding: 0,
    },
    content: {
      paddingTop: theme.spacing(8),
      paddingBottom: theme.spacing(20),

      '& .slider-frame': {
        border: '2px solid transparent',
        '&:focus-visible': {
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: theme.shape.borderRadius,
        },
      },
      [theme.breakpoints.up('sm')]: {
        paddingBottom: theme.spacing(8),
      },
    },
    contentInTab: {
      paddingTop: theme.spacing(4),
    },
    contentCondensed: {
      padding: 0,
    },
    navBtn: {},
    navBtnSmall: {
      height: 24,
      width: 24,
    },
    navBtnLarge: {
      height: 32,
      width: 32,
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
    },
    navBtnLeft: {
      transform: 'translateX(-40px)',
    },
    mobileBottomControls: {
      width: 105,
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      bottom: -44,
      justifyContent: 'space-between',
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    mobileBottomControlsHeroCard: {
      display: 'flex',
    },
    icon: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.primary.main,
      width: '1em',
    },
    iconDisabled: {
      color: theme.palette.grey[500],
    },
    cartRecommended: {
      backgroundColor: theme.palette.background.grey,
      marginBottom: theme.spacing(20),
    },
    card: {
      [theme.breakpoints.up('sm')]: {
        paddingRight: theme.spacing(10),
      },
    },
    productHeroCardContent: {
      padding: 0,
      [theme.breakpoints.down('sm')]: {
        touchAction: 'pan-y',
      },
      [`& .${classes.productHeroTile} button`]: {
        '@media (min-width: 900px) and (max-width: 1300px)': {
          whiteSpace: 'normal',
        },
      },
    },
    productHeroTile: {
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0, 4),
      },
    },
  }
})

interface ProductCarouselControlsConfig {
  products: CarouselProduct[]
  creativeSlot: string
  carouselType: CarouselProductTypes
  pageType?: string | null | undefined
  pageTypeDetails?: string | null | undefined
  productHeroCard?: boolean
}

type ProductCarouselControlProps = Pick<
  ControlProps,
  | 'currentSlide'
  | 'slidesToShow'
  | 'slideCount'
  | 'previousSlide'
  | 'nextSlide'
  | 'slidesToScroll'
>

const useProductCarouselControls = ({
  products,
  creativeSlot,
  carouselType,
  pageType,
  pageTypeDetails,
  productHeroCard,
}: ProductCarouselControlsConfig) => {
  const { classes, cx } = useStyles()
  return {
    renderCenterLeftControls: (props: ProductCarouselControlProps) => {
      const disabled = props.currentSlide === 0
      return (
        <>
          {!productHeroCard && (
            <ButtonBase
              className={cx(
                classes.navBtn,
                classes.navBtnLarge,
                classes.navBtnLeft
              )}
              disabled={disabled}
              onClick={() =>
                handleSlideMovement(
                  props,
                  products,
                  'prev',
                  creativeSlot,
                  carouselType,
                  pageType,
                  pageTypeDetails
                )
              }
              aria-label="previous"
            >
              <CarouselArrowLeftIcon
                className={cx(classes.icon, {
                  [classes.iconDisabled]: disabled,
                })}
              />
            </ButtonBase>
          )}
        </>
      )
    },
    renderCenterRightControls: (props: ProductCarouselControlProps) => {
      const disabled =
        props.currentSlide + props.slidesToShow === props.slideCount ||
        props.slideCount < props.slidesToShow
      return (
        <>
          {!productHeroCard && (
            <ButtonBase
              disabled={disabled}
              className={cx(classes.navBtn, classes.navBtnLarge)}
              onClick={() =>
                handleSlideMovement(
                  props,
                  products,
                  'next',
                  creativeSlot,
                  carouselType,
                  pageType,
                  pageTypeDetails
                )
              }
              aria-label="next"
            >
              <CarouselArrowRightIcon
                className={cx(classes.icon, {
                  [classes.iconDisabled]: !!props.slidesToScroll && disabled,
                })}
              />
            </ButtonBase>
          )}
        </>
      )
    },
    renderBottomCenterControls: (props: ProductCarouselControlProps) => {
      const previousDisabled = props.currentSlide === 0
      const nextDisabled =
        props.currentSlide + props.slidesToShow === props.slideCount ||
        props.slideCount < props.slidesToShow
      return (
        <div
          className={cx(
            classes.mobileBottomControls,
            productHeroCard && classes.mobileBottomControlsHeroCard
          )}
        >
          <ButtonBase
            disabled={previousDisabled}
            onClick={() =>
              handleSlideMovement(
                props,
                products,
                'prev',
                creativeSlot,
                carouselType,
                pageType,
                pageTypeDetails
              )
            }
            aria-label="previous"
            className={cx(classes.navBtn, classes.navBtnSmall)}
          >
            <CarouselArrowLeftIcon
              className={cx(classes.icon, {
                [classes.iconDisabled]: previousDisabled,
              })}
            />
          </ButtonBase>
          <Typography variant="body2">
            {props.currentSlide + 1} of {props.slideCount}
          </Typography>
          <ButtonBase
            disabled={nextDisabled}
            onClick={() =>
              handleSlideMovement(
                props,
                products,
                'next',
                creativeSlot,
                carouselType,
                pageType,
                pageTypeDetails
              )
            }
            aria-label="next"
            className={cx(classes.navBtn, classes.navBtnSmall)}
          >
            <CarouselArrowRightIcon
              className={cx(classes.icon, {
                [classes.iconDisabled]: !!props.slidesToScroll && nextDisabled,
              })}
            />
          </ButtonBase>
        </div>
      )
    },
  }
}

const NewPdpDynamicProductCarousel: React.FC<ProductCardProps> = ({
  type,
  products,
  withoutControls,
  slidesToShow,
  condensed,
  preloadCardImages,
  title,
  productHeroCard = false,
  isProductRecommendation = false,
  hideProductImg = false,
  displayDiscount,
  setRef,
}) => {
  const { classes, cx } = useStyles()
  const getSize = useResponsiveSizes()

  const pageType = useCategorizePage()
  const creativeSlot = `${type} - ${pageType}`

  const carouselControls = useProductCarouselControls({
    products,
    carouselType: type,
    creativeSlot,
    productHeroCard,
  })

  const isCartRecommended =
    type === CarouselProductTypes.CartRecommendedProducts

  const numProducts = getSize({ xs: 2, sm: 3, md: 5, lg: 6 })

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true

  const carouselRef = useRef(null)
  const [sawCarousel, setSawCarousel] = useState(false)
  const carouselVisbility = useIntersectionObserver(carouselRef, {
    threshold: INTERSECTION_OBSERVER_THRESHOLD,
  })

  if (!products || !products.length) return null

  if (carouselVisbility?.isIntersecting && !sawCarousel) {
    const slicedVisible = products.slice(0, numProducts)

    sendCarouselPromoEvent({
      creativeSlot,
      carouselType: type,
      products: slicedVisible,
    })

    setSawCarousel(true)
  }

  return (
    <div
      data-testid={type}
      id={type}
      className={cx({
        [classes.cartRecommended]: isCartRecommended,
      })}
      ref={setRef && setRef('recProducts')}
    >
      <Container
        maxWidth="lg"
        className={cx({
          [classes.withoutControls]: withoutControls,
        })}
        style={{ paddingBottom: '0 !important' }}
      >
        <div
          ref={carouselRef}
          className={cx(
            classes.content,
            {
              [classes.contentCondensed]: condensed,
            },
            productHeroCard && classes.productHeroCardContent,
            isProductRecommendation && classes.contentInTab
          )}
        >
          <CarouselTitle title={title} isHero={productHeroCard} type={type} />
          <Carousel
            slidesToScroll={
              productHeroCard && isDesktop ? 2 : slidesToShow || numProducts
            }
            slidesToShow={
              productHeroCard && isDesktop ? 2 : slidesToShow || numProducts
            }
            disableEdgeSwiping
            withoutControls={!isDesktop}
            {...carouselControls}
          >
            {products.map((product, i) => (
              <NewPdpDynamicProductCard
                key={i}
                index={i}
                type={type}
                product={product}
                testId={type}
                preloadCardImages={preloadCardImages}
                className={cx(
                  classes.card,
                  productHeroCard && classes.productHeroTile
                )}
                hideProductImg={hideProductImg}
                displayDiscount={displayDiscount}
              />
            ))}
          </Carousel>
        </div>
      </Container>
    </div>
  )
}

export default NewPdpDynamicProductCarousel
